import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.js';
import { AccountInactiveError, InvalidCredentialsError } from '../auth/authService.js';
import { formatBuildLabel, formatBuildTooltip } from '../shell/buildInfo.js';

const SSO_ERROR_MESSAGES: Record<string, string> = {
  not_invited: "Your account hasn't been set up yet. Ask your admin to invite you.",
  wrong_domain: 'Only @aptitudemedical.com Google accounts can sign in here.',
  deactivated: 'Your account has been deactivated. Contact your admin.',
  oauth_failed: 'Google sign-in failed. Try again, or use email + password.',
  sso_disabled: 'Google sign-in is temporarily disabled.',
  // Plan 16 polish: same-origin API client saw `403 csrf_invalid` or `csrf_missing`
  // and redirected here after invalidating the CsrfManager.
  csrf_expired: 'Your session has expired or was invalidated. Please sign in again.',
};

export function LoginPage(): JSX.Element {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const ssoErrorCode = params.get('error');
  const ssoErrorMessage = ssoErrorCode ? SSO_ERROR_MESSAGES[ssoErrorCode] : null;
  const returnTo = params.get('returnTo') ?? '/';
  const apiBase = import.meta.env.VITE_API_URL ?? '';
  const googleHref = `${apiBase}/auth/google?returnTo=${encodeURIComponent(returnTo)}`;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setPending(true);
    try {
      await login(email, password);
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof InvalidCredentialsError) {
        setError('Invalid credentials. Check your email and password.');
      } else if (err instanceof AccountInactiveError) {
        setError('Your account is not active. Contact your admin.');
      } else {
        setError('Sign-in failed. Please try again.');
      }
    } finally {
      setPending(false);
    }
  }

  function onSubmit(event: FormEvent<HTMLFormElement>): void {
    void handleSubmit(event);
  }

  return (
    <main className="login-view">
      <h1>Sign in</h1>
      {ssoErrorMessage && (
        <div role="alert" className="login-sso-error-banner">
          {ssoErrorMessage}
        </div>
      )}
      <a href={googleHref} className="login-google-button" data-testid="login-google-button">
        Sign in with Google
      </a>
      <div className="login-divider">— or —</div>
      <form onSubmit={onSubmit}>
        <label>
          Email
          <input
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label>
          Password
          <input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        {error !== null && (
          <p role="alert" className="error">
            {error}
          </p>
        )}
        <button type="submit" disabled={pending}>
          {pending ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
      <p className="login-build" title={formatBuildTooltip()}>
        {formatBuildLabel()}
      </p>
    </main>
  );
}
